import { clsx } from 'clsx';
import { DetailedHTMLProps, InputHTMLAttributes, ReactNode, useId, forwardRef } from 'react';
import { Text } from '../../foundations';
import styles from './InputText.module.css';
import { FormErrorMessage } from '../form-error-message';

export type InputTextProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
	/**
	 * Indicates the type of input to be rendered
	 * @default text
	 */
	type?: InputTextType;

	/**
	 * Indicates the text to be used in the input label
	 * @type string
	 */
	label: string;

	/**
	 * Description of the property
	 * @type string
	 */
	name: string;

	/**
	 * Indicates error message to be displayed
	 */
	errorMessage?: ReactNode;

	/**
	 * Indicates information message to be displayed
	 */
	information?: ReactNode;

	/**
	 * Add classes by means of props
	 */
	className?: string;

	/**
	 * Add styles to the label using props
	 */
	labelStyles?: string;
};

type InputTextType = 'text' | 'email' | 'number';

export const InputText = forwardRef<HTMLInputElement, InputTextProps>(
	({ label, labelStyles, errorMessage, information, name, id, type, className, ...props }, ref) => {
		const generateIdentifier = useId();
		const identifier = id ?? generateIdentifier;
		const errorMessageIdentifier = errorMessage ? identifier + '-error' : undefined;
		label = label.charAt(0).toUpperCase() + label.slice(1);

		return (
			<div className={styles.contentInput}>
				<input
					className={clsx(styles.input, { [styles.errorBorder]: Boolean(errorMessage) }, className)}
					placeholder=" "
					ref={ref}
					type={type}
					name={name}
					id={identifier + '-input'}
					aria-errormessage={errorMessageIdentifier}
					aria-invalid={Boolean(errorMessage)}
					{...props}
				/>
				<label className={clsx(styles.label, labelStyles)} htmlFor={identifier + '-input'}>
					{label}
				</label>
				{errorMessage && <FormErrorMessage id={errorMessageIdentifier}>{errorMessage}</FormErrorMessage>}
				{information && (
					<Text className={styles.information} as="span" font="verdana" size="lg-regular">
						{information}
					</Text>
				)}
			</div>
		);
	}
);

InputText.displayName = 'InputText';
